import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import { CssBaseline, CssVarsProvider } from '@mui/joy';
import { THEME_ID, ThemeProvider } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import type { QueryClient } from '@tanstack/react-query';
// eslint-disable-next-line import-x/no-extraneous-dependencies
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { createRootRouteWithContext, HeadContent, Scripts } from '@tanstack/react-router';
// eslint-disable-next-line import-x/no-extraneous-dependencies
import { TanStackRouterDevtools } from '@tanstack/react-router-devtools';
import { createServerFn } from '@tanstack/react-start';
import type { ReactNode } from 'react';
import { Fragment } from 'react';

import globalCss from '@/assets/css/global.css?url';
import maplibreGlCss from '@/assets/css/maplibre-gl.css?url';
import { DefaultCatchBoundary } from '@/components/router/DefaultCatchBoundary';
import { PendingComponent } from '@/components/router/PendingComponent';
import { META_DESCRIPTION, META_ROBOTS_NOINDEX, META_TITLE_CONTENT } from '@/modules/META';
import { zSharedEnv } from '@/schemas/zSharedEnv';
import { materialTheme, theme } from '@/setup/theme';

import '@/setup/dayjs';

const sharedEnv = createServerFn({ method: 'GET' }).handler(async () => zSharedEnv.parse(process.env));

export const Route = createRootRouteWithContext<{
  queryClient: QueryClient;
}>()({
  beforeLoad: async () => {
    const env = await sharedEnv();

    return { env };
  },
  head: ({ match }) => ({
    meta: [
      { title: META_TITLE_CONTENT },
      META_DESCRIPTION,
      ...(match.context.env.SHARED_ROBOTS_NOINDEX ? [META_ROBOTS_NOINDEX] : []),
      { charSet: 'utf-8' },
      { name: 'viewport', content: 'width=device-width, initial-scale=1' },
      { name: 'theme-color', content: '#dcdcdc' },
      { name: 'sentry-environment', content: match.context.env.SHARED_SENTRY_ENVIRONMENT },
      { name: 'emotion-insertion-point', content: '' },
    ],
    links: [
      {
        rel: 'icon',
        type: 'image/svg+xml',
        href: '/favicon.svg',
      },
      {
        rel: 'preconnect',
        href: 'https://sgx.geodatenzentrum.de',
        crossOrigin: 'anonymous',
      },
      { rel: 'stylesheet', href: globalCss },
      { rel: 'stylesheet', href: maplibreGlCss },
    ],
  }),

  scripts: ({ match }) => [
    ...(match.context.env.SHARED_UMAMI_WEBSITE_ID
      ? [
          {
            defer: true,
            'data-website-id': match.context.env.SHARED_UMAMI_WEBSITE_ID,
            src: '/umami/script.js',
          },
        ]
      : []),
  ],

  errorComponent: DefaultCatchBoundary,
  pendingComponent: PendingComponent,
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  shellComponent: RootDocument,
});

function Providers({ children }: { children: ReactNode }) {
  const emotionCache = createCache({ key: 'mui-emotion' });

  return (
    <LocalizationProvider adapterLocale="de" dateAdapter={AdapterDayjs}>
      <CacheProvider value={emotionCache}>
        <ThemeProvider theme={{ [THEME_ID]: materialTheme }}>
          <CssVarsProvider theme={theme}>
            <CssBaseline />
            {children}
          </CssVarsProvider>
        </ThemeProvider>
      </CacheProvider>
    </LocalizationProvider>
  );
}

function RootDocument({ children }: { children: ReactNode }) {
  return (
    <html lang="de">
      <head>
        <HeadContent />
      </head>
      <body>
        <noscript>Um diese Anwendung zu verwenden, müssen Sie JavaScript in ihrem Browser aktivieren.</noscript>

        <Providers>{children}</Providers>

        {!import.meta.env.PROD && import.meta.env.VITE_DEV_TOOLS === 'true' && (
          <Fragment>
            <TanStackRouterDevtools position="bottom-right" />
            <ReactQueryDevtools buttonPosition="bottom-left" />
          </Fragment>
        )}

        <Scripts />
      </body>
    </html>
  );
}
